import React, { useState, useEffect, useRef } from "react";
import _, { uniqBy } from "lodash";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import actions from "../actions";
import { getDisplayNameFromKey } from "../utils";
import { CoreReportGeohierarchy, MASTER_FILTER_KEYS } from "../constants";
import ChartSection from "./CoreReports/ChartSection";
import Portal from "../../common/components/Portal";
import { ExportModal } from "./ExportModal";
import ReactTooltip from "react-tooltip";
import FilterIcon from "../../../assets/img/dashboard/filter-butn.svg";
import { getSelectedFilterValuesFormIds, isFilterCleared, removeMfilterEmptyKeys, removeWildCardOrYear } from "./SmartchartUtils";
import dashboardActions from "../../dashboard/actions.js";
import { fieldMatchesFilterWildcardKey, isDateFieldType } from "../../dashboard/filterFieldBuilders";
import HeaderConfigModal from "./HeaderConfigModal/HeaderConfigModal.jsx";
import { resolveDefaultClientFromList } from "../../../utils/dashboardSession";
import { showAlert as showAlertUtil } from "../../../config/utils";

const CreateReportFromCoreReportsModal = ({ onCancel, selectedCoreReport, mFilters, isModalView = false, changeTabsInFavModal }) => {
    const corereportExport = useRef(null);
    const {
        getClientDropDownDataResponse,
        getTemplatePropertiesListResponse,
        getTemplateListResponse,
        masterFilterList,
        exportCoreReportDataResponse
    } = useSelector(state => state.smartChartReducer) || {};
    const history = useHistory();

    const [selectedClient, setSelectedClient] = useState("");
    const [selectedExportProperty, setSelectedExportProperty] = useState("");
    const [selectedExportTemplate, setSelectedExportTemplate] = useState("");
    const [filterOpen, setFilterOpen] = useState(false);
    const [showFilter, setShowFilter] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { filterFields, getDashboard } = useSelector(state => state?.dashboardReducer);
    const [yearField, setYearFields] = useState([]);
    const [showHeadingConfig, setShowHeadingConfig] = useState(false);

    const [chartData, setChartData] = useState({
        name: "",
        notes: "",
        properties_text: {
            project: {
                band1: {
                    mfilter: {},
                    type: {}
                }
            },
            assets: {
                band1: {
                    mfilter: {},
                    type: {}
                }
            },
            energy_mng: {
                band1: {
                    mfilter: {},
                    type: {}
                }
            }
        }
    });
    const [showErrorBorder, setShowErrorBorder] = useState(false);
    const [requiredFields, setRequiredFields] = useState({
        project: [],
        energy_mng: [],
        assets: []
    });
    const [showExportTypeSelectModal, setShowExportTypeSelectModal] = useState(false);
    const changeDefaultProjectRef = useRef(true);

    const dispatch = useDispatch();
    useEffect(() => {
        if (selectedCoreReport) {
            let updatedPropertyData = JSON.parse(selectedCoreReport.properties_text);
            if (
                updatedPropertyData?.project?.band1?.type?.hasOwnProperty("energy_band") ||
                updatedPropertyData?.energy_mng?.band1?.type?.hasOwnProperty("energy_band")
            ) {
                updatedPropertyData = removeExtrakeysFromEnergyBand(updatedPropertyData);
            }
            let propertyData = updatedPropertyData;
            setChartData(prevChartData => {
                return {
                    ...prevChartData,
                    properties_text: propertyData
                };
            });
            setRequiredFields({
                project: [...Object.keys(propertyData?.project?.band1?.core_report_req_filters || {})],
                energy_mng: [...Object.keys(propertyData?.energy_mng?.band1?.mfilter || {})],
                assets: [...Object.keys(propertyData?.assets?.band1?.mfilter || {})]
            });
        }
        dispatch(actions.getClientDropDownData());
    }, []);
    useEffect(() => {
        const timer2 = setTimeout(() => {
            const { start, end } = getDashboard?.years || {};
            if (start && end) {
                let yearFilters = [];
                const yearsArray = Array.from({ length: end - start + 1 }, (_, index) => start + index);
                yearFilters = yearsArray.map(year => ({
                    label: year,
                    key: year,
                    paramKey: `maintenance_years.${year}`,
                    isWildCardFilter: true,
                    fieldType: "number"
                }));
                setYearFields(yearFilters);
            }
        }, 100);

        return () => {
            clearTimeout(timer2);
        };
    }, [getDashboard]);
    useEffect(() => {
        if (corereportExport?.current && exportCoreReportDataResponse) {
            const permissionError = exportCoreReportDataResponse?.detail;
            if (exportCoreReportDataResponse?.success) {
                showLongAlert(
                    "Report generation initiated and will be ready for download shortly. Please click the Refresh button to check for status."
                );
                onCancel();
                if (isModalView) {
                    changeTabsInFavModal("reports");
                } else {
                    history.push("/smartcharts/reports");
                }
            } else {
                showLongAlert(typeof permissionError === "string" ? permissionError : exportCoreReportDataResponse?.error);
            }
            corereportExport.current = false;
        }
    }, [exportCoreReportDataResponse]);
    useEffect(() => {
        let clientList = getClientDropDownDataResponse?.data || [];
        if (clientList.length) {
            let defaultClient = mFilters?.client_ids?.[0] ? { id: mFilters?.client_ids?.[0] } : resolveDefaultClientFromList(clientList);
            if (defaultClient) {
                setSelectedClient(defaultClient.id);
                dispatch(actions.getTemplatePropertiesList({ client_id: defaultClient.id }));
                dispatch(actions.getTemplateList({ client_id: defaultClient.id }));
                getMasterFiltersData("projects", "project", {
                    client_id: defaultClient?.id || null,
                    user_id: localStorage.getItem("userId") || ""
                });
            }
        }
    }, [getClientDropDownDataResponse]);

    useEffect(() => {
        if (selectedCoreReport && selectedCoreReport.template_property_id) {
            setSelectedExportProperty(selectedCoreReport.template_property_id);
        } else {
            let propertyList = getTemplatePropertiesListResponse?.data || [];
            if (propertyList.length) {
                let defaultProperty =
                    propertyList.find(property => property.sm_default === true) || propertyList.find(property => property.default === true);
                if (defaultProperty) {
                    setSelectedExportProperty(defaultProperty.id);
                }
            }
        }

        if (selectedCoreReport && selectedCoreReport.template_id) {
            setSelectedExportTemplate(selectedCoreReport.template_id);
        } else {
            let templateList = getTemplateListResponse?.data || [];
            if (templateList.length) {
                let defaultTemplate =
                    templateList.find(property => property.sm_default === true) || templateList.find(property => property.default === true);
                if (defaultTemplate) {
                    setSelectedExportTemplate(defaultTemplate.id);
                }
            }
        }
    }, [getTemplatePropertiesListResponse, getTemplateListResponse, selectedCoreReport]);

    useEffect(() => {
        let projectList = masterFilterList?.project?.projects || [];
        if (projectList.length) {
            changeDefaultProjectRef.current = false;
            let selectedProjectIds = [];
            if (projectList.length) {
                selectedProjectIds = [projectList[0]?.id];
                setSelectedMasterFilters("", {
                    mfilterKey: "project_ids",
                    bandName: "band1",
                    filterValues: selectedProjectIds,
                    entity: "project",
                    filterKey: "projects",
                    dropDownData: [...projectList] || [],
                    filterLabel: "Projects",
                    isFiltersKey: "filter&topn"
                });
            }
        }
        let propertyData = JSON.parse(selectedCoreReport.properties_text);
        Object.keys(propertyData || {}).forEach(entity => {
            const coreReportFilters = propertyData[entity]?.band1?.core_report_req_filters;
            if (coreReportFilters) {
                Object.keys(coreReportFilters || {}).forEach(filterKey => {
                    const filter = coreReportFilters[filterKey];
                    if (filter && filter.keep_values) {
                        const entityFilters = MASTER_FILTER_KEYS[entity]?.find(e => e?.paramKey === filterKey);
                        setSelectedMasterFilters("", {
                            mfilterKey: filterKey,
                            bandName: "band1",
                            filterValues: propertyData[entity]?.band1?.mfilter[filterKey],
                            entity: entity,
                            filterKey: entityFilters?.key,
                            filterLabel: entityFilters?.key,
                            isFiltersKey:
                                Object.keys(propertyData[entity]?.band1?.mfilter?.filters || {})?.length > 0 &&
                                propertyData[entity]?.band1?.mfilter?.top_n
                                    ? "filter&topn"
                                    : Object.keys(propertyData[entity]?.band1?.mfilter?.filters || {})?.length > 0
                                    ? "filter"
                                    : propertyData[entity]?.band1?.mfilter?.top_n
                                    ? "top_n"
                                    : "",
                            dropDownData: null
                        });
                    }
                });
            }
        });
    }, [masterFilterList?.project?.projects]);

    const handleSelectClient = async value => {
        setSelectedClient(value);
        setSelectedExportProperty("");
        if (value) {
            changeDefaultProjectRef.current = true;
            dispatch(actions.getTemplatePropertiesList({ client_id: value }));
        }
    };

    const getMasterFiltersData = (key, entity, params) => {
        dispatch(actions.getSmartChartMasterFilterDropDown(key, entity, params));
    };

    const setSelectedMasterFilters = (actionType, params = {}) => {
        const {
            bandName,
            mfilterKey = "",
            filterValues = [],
            entity,
            filterKey,
            dropDownData = [],
            filterLabel,
            isFilterValue = false,
            removeWildcardFilter = false,
            removeYearFilter = false,
            isFiltersKey = "",
            isArray = false,
            paramKey = "",
            isYearFilter = false
        } = params;
        if (entity === "project" && mfilterKey === "years") {
            setChartData(prevState => {
                return {
                    ...prevState,
                    properties_text: {
                        ...prevState.properties_text,
                        [entity]: {
                            ...prevState.properties_text[entity],
                            [bandName]: {
                                ...prevState.properties_text[entity][bandName],
                                mfilter:
                                    isYearFilter && paramKey === "start_year"
                                        ? {
                                              ...prevState.properties_text[entity][bandName]?.mfilter,
                                              start_year: paramKey === "start_year" && filterValues.start
                                          }
                                        : isYearFilter && paramKey === "end_year"
                                        ? {
                                              ...prevState.properties_text[entity][bandName]?.mfilter,
                                              end_year: paramKey === "end_year" && filterValues.end
                                          }
                                        : isYearFilter && paramKey === "update_fci"
                                        ? {
                                              ...prevState.properties_text[entity][bandName]?.mfilter,
                                              update_fci: filterValues.update_fci
                                          }
                                        : {
                                              ...removeMfilterEmptyKeys({
                                                  ...prevState.properties_text[entity][bandName]?.mfilter,
                                                  start: filterValues.start,
                                                  end: filterValues.end
                                              })
                                          },
                                filter_label: prevState.properties_text[entity][bandName]?.filter_label
                                    ? {
                                          ...removeMfilterEmptyKeys({
                                              ...prevState.properties_text[entity][bandName].filter_label,
                                              ...(paramKey === "start_year" && {
                                                  "Start Year": filterValues.start ? [filterValues.start] : []
                                              }),
                                              ...(paramKey === "end_year" && { ["End Year"]: filterValues.end ? [filterValues.end] : [] }),
                                              ...(paramKey === "update_fci" && { "Static FCI": filterValues.update_fci ? ["Yes"] : [] })
                                          })
                                      }
                                    : {
                                          ...removeMfilterEmptyKeys({
                                              ...(paramKey === "start_year" && {
                                                  "Start Year": filterValues.start ? [filterValues.start] : []
                                              }),
                                              ...(paramKey === "end_year" && { ["End Year"]: filterValues.end ? [filterValues.end] : [] }),
                                              ...(paramKey === "update_fci" && { "Static FCI": filterValues.update_fci ? ["Yes"] : [] })
                                          })
                                      }
                            }
                        }
                    }
                };
            });
        } else if (mfilterKey === "energy_mng_custom_years") {
            setChartData(prevState => {
                return {
                    ...prevState,
                    properties_text: {
                        ...prevState.properties_text,
                        [entity]: {
                            ...prevState.properties_text[entity],
                            [bandName]: {
                                ...prevState.properties_text[entity][bandName],
                                mfilter: {
                                    ...removeMfilterEmptyKeys({
                                        ...prevState.properties_text[entity][bandName].mfilter,
                                        ...filterValues
                                    })
                                }
                            }
                        }
                    }
                };
            });
        } else {
            if (isFilterValue) {
                setChartData(prevState => {
                    return isFilterCleared(prevState, bandName, entity);
                });
            }
            if (removeWildcardFilter || removeYearFilter) {
                setChartData(prevState => {
                    return removeWildCardOrYear(prevState, bandName, entity, isArray);
                });
            }
            let filterNames = [];
            let topName = [];
            if (filterLabel === "filters" || filterLabel === "top_n") {
                uniqBy(filterFields, "key").forEach(fi => {
                    if (filterLabel === "filters") {
                        Object.entries(filterValues || {}).forEach(([wKey, wValue]) => {
                            if (fieldMatchesFilterWildcardKey(fi, wKey)) {
                                filterNames.push({
                                    name: fi.label,
                                    value:
                                        isDateFieldType(fi.fieldType)
                                            ? [`${wValue?.filters?.[0]} ${wValue?.key?.from}-${wValue?.key?.to}`]
                                            : [`${wValue?.key || ""} [${wValue?.filters?.join(",")}]`]
                                });
                            }
                        });
                    } else if (filterLabel === "top_n") {
                        Object.entries(filterValues || {}).forEach(([wKey, wValue]) => {
                            if (fieldMatchesFilterWildcardKey(fi, wKey)) {
                                topName.push({
                                    name: fi.label,
                                    value: [`${wValue || ""} [Top N]`]
                                });
                            }
                        });
                    }
                    return filterNames;
                });
            }

            setChartData(prevState => {
                return {
                    ...prevState,
                    properties_text: {
                        ...prevState.properties_text,
                        [entity]: {
                            ...prevState.properties_text[entity],
                            [bandName]: {
                                ...prevState.properties_text[entity][bandName],
                                mfilter: {
                                    ...(filterKey === "filters" || filterKey === "top_n"
                                        ? {
                                              ...prevState.properties_text[entity][bandName].mfilter,
                                              [mfilterKey]: filterValues?.length > 0 ? [...filterValues] : filterValues
                                          }
                                        : removeMfilterEmptyKeys(
                                              {
                                                  ...prevState.properties_text[entity][bandName].mfilter,
                                                  [mfilterKey]:
                                                      Array.isArray(dropDownData) &&
                                                      filterKey !== "projects" &&
                                                      filterKey !== "client_ids" &&
                                                      filterKey !== "clients"
                                                          ? getSelectedFilterValuesFormIds(dropDownData, filterValues)
                                                          : filterValues?.length > 0
                                                          ? [...filterValues]
                                                          : filterValues
                                              },
                                              isFiltersKey
                                          ))
                                },
                                filter_label: prevState.properties_text[entity][bandName]?.filter_label
                                    ? {
                                          ...(filterLabel === "filters"
                                              ? {
                                                    ...removeMfilterEmptyKeys({
                                                        ...Object.fromEntries(
                                                            Object.entries(prevState?.properties_text?.[entity]?.[bandName]?.filter_label || {})
                                                                .filter(([key, value]) => {
                                                                    const wildCardLabels = [...(filterFields || []), ...(yearField || [])]
                                                                        .filter(e => e.isWildCardFilter === true)
                                                                        .map(e => String(e.label));
                                                                    return !wildCardLabels.includes(String(key));
                                                                })
                                                                .map(([key, value]) => [key, Array.isArray(value) ? value : [value]])
                                                        ),
                                                        ...filterNames?.reduce((acc, e) => {
                                                            acc[e.name] = e.value;
                                                            return acc;
                                                        }, {})
                                                    })
                                                }
                                              : filterLabel === "top_n"
                                              ? {
                                                    ...prevState.properties_text[entity][bandName].filter_label,
                                                    ...topName?.reduce((acc, e) => {
                                                        acc[e.name] = e.value;
                                                        return acc;
                                                    }, {})
                                                }
                                              : {
                                                    ...removeMfilterEmptyKeys({
                                                        ...(prevState?.properties_text?.[entity]?.[bandName]?.filter_label || {}),
                                                        ...(Array.isArray(dropDownData) && {
                                                            [filterLabel === "FCA Projects" && filterValues?.length && dropDownData?.length
                                                                ? "Projects"
                                                                : filterLabel]: getSelectedFilterNameFormIds(dropDownData, filterValues)
                                                        })
                                                    })
                                                })
                                      }
                                    : {
                                          ...removeMfilterEmptyKeys({
                                              [filterLabel]: [...getSelectedFilterNameFormIds(dropDownData, filterValues)]
                                          })
                                      }
                            }
                        }
                    }
                };
            });
        }
    };

    const removeMfilterKeys = key => {
        switch (key) {
            case "projects":
                return {
                    region_ids: [],
                    site_ids: [],
                    building_types: [],
                    building_ids: [],
                    start: "",
                    end: "",
                    fmp: [],
                    infrastructure_requests: [],
                    addition_ids: [],
                    color_scale: [],
                    responsibility_ids: []
                };
            case "regions":
                return {
                    // site_ids: [],
                    // building_types: [],
                    // building_ids: [],
                    fmp: [],
                    infrastructure_requests: [],
                    addition_ids: [],
                    color_scale: []
                };
            case "sites":
                return {
                    // building_types: [],
                    // building_ids: [],
                    fmp: [],
                    infrastructure_requests: [],
                    addition_ids: [],
                    color_scale: []
                };
            case "energy_mng_regions":
                return {
                    site_ids: [],
                    building_types: [],
                    building_ids: [],
                    years: []
                };
            case "energy_mng_sites":
                return {
                    building_types: [],
                    building_ids: [],
                    years: []
                };
            case "energy_mng_buildings":
                return {
                    years: []
                };
            case "asset_regions":
                return {
                    site_ids: [],
                    // building_types: [],
                    building_ids: []
                };
            case "asset_sites":
                return {
                    // building_types: [],
                    building_ids: []
                };
            default:
                return {};
        }
    };

    const removeExtrakeysFromEnergyBand = tempPropertyData => {
        if (tempPropertyData?.project?.band1?.type?.hasOwnProperty("energy_band")) {
            delete tempPropertyData?.project?.band1?.type["em_eci_site_cost"];
            delete tempPropertyData?.project?.band1?.type["em_eci_energy_cost"];
            delete tempPropertyData?.project?.band1?.type["bld_brkdown_energy_cost"];
        }
        return tempPropertyData;
    };

    const getSelectedFilterNameFormIds = (dropDownData = [], selectedIds = []) => {
        let filteredNames = [];
        if (selectedIds.length) {
            dropDownData.map(data => {
                if (selectedIds.includes(data.id)) {
                    filteredNames.push(data.name + (data.description ? " (" + data.description + ")" : ""));
                }
            });
        }
        return filteredNames;
    };

    const validate = () => {
        setShowErrorBorder(false);
        if (!selectedClient?.trim()?.length) {
            setShowErrorBorder(true);
            return false;
        }
        if (!selectedExportProperty?.trim()?.length) {
            setShowErrorBorder(true);
            return false;
        }
        if (!selectedExportTemplate?.trim()?.length) {
            setShowErrorBorder(true);
            return false;
        }
        if (!chartData.name && !chartData.name.trim().length) {
            setShowErrorBorder(true);
            return false;
        }
        if (chartData.name && chartData.name.trim().length > 100) {
            setShowErrorBorder(true);
            return false;
        }
        if (!checkRequiredFilterSelected()) {
            return false;
        }
        return true;
    };

    const checkRequiredFilterSelected = () => {
        let propertyData = JSON.parse(selectedCoreReport.properties_text);
        let lastValidationError = null;
        for (let entity of Object.keys(propertyData)) {
            const coreReportFilters = propertyData[entity]?.band1?.core_report_req_filters;
            if (coreReportFilters) {
                for (let filterKey of Object.keys(coreReportFilters)) {
                    const filter = coreReportFilters[filterKey];
                    if (filter && filter.required) {
                        const dataExists =
                            chartData?.properties_text?.[entity]?.band1?.mfilter?.[filterKey] ||
                            chartData?.properties_text?.[entity]?.band1?.mfilter?.filters?.[filterKey];
                        const filterKeyName = filterFields?.find(data => data?.paramKey === filterKey);
                        const isFilterKey = chartData?.properties_text?.[entity]?.band1?.mfilter?.filters?.[filterKey]?.filters || [];
                        const isFilterKeyValue = chartData?.properties_text?.[entity]?.band1?.mfilter?.filters?.[filterKey]?.key;
                        const Filter = chartData?.properties_text?.[entity]?.band1?.mfilter;
                        if (
                            (!dataExists && filterKey !== "year") ||
                            dataExists?.length === 0 ||
                            (isFilterKey?.length > 0 &&
                                !(isFilterKey[0] === "not_null" || isFilterKey[0] === "null" || isFilterKey[0] === "count_gt_1") &&
                                isFilterKeyValue === "") ||
                            (filterKey === "year" && !Filter?.start_year && !Filter?.end_year)
                        ) {
                            lastValidationError = `Required data missing for filter ${filterKeyName?.label} in ${entity}`;
                            showLongAlert(lastValidationError);
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    };

    const exportReport = () => {
        if (validate()) {
            setShowExportTypeSelectModal(true);
        }
    };

    const exportReportConfirm = async (exportType = 1, selectedExportType, selectedFileExtension, restParams) => {
        let tempSaveChartData = { ...chartData };
        ["project", "energy_mng", "assets"]?.forEach(
            bandKey =>
                (tempSaveChartData.properties_text[bandKey].band1.mfilter = {
                    ...tempSaveChartData?.properties_text?.[bandKey]?.band1?.mfilter,
                    client_ids: [selectedClient]
                })
        );
        let updatedProjectDataWithDocs = updateDataWithUserDocs({ ...tempSaveChartData.properties_text?.project?.band1?.type }, "project");
        let updatedEnergyMngDataWithDocs = updateDataWithUserDocs({ ...tempSaveChartData.properties_text?.energy_mng?.band1?.type }, "energy_mng");
        let updatedAssetMngDataWithDocs = updateDataWithUserDocs({ ...tempSaveChartData.properties_text?.assets?.band1?.type }, "assets");
        if (tempSaveChartData?.properties_text?.project?.band1?.mfilter && tempSaveChartData.properties_text?.project?.band1?.type?.sorted_recom) {
            tempSaveChartData.properties_text.project.band1.type.sorted_recom.band1.mfilter = {
                ...tempSaveChartData.properties_text.project.band1.mfilter
            };
        }

        tempSaveChartData.properties_text.project.band1.type = {
            ...updatedProjectDataWithDocs
        };
        tempSaveChartData.properties_text.energy_mng.band1.type = {
            ...updatedEnergyMngDataWithDocs
        };

        tempSaveChartData.properties_text.assets.band1.type = {
            ...updatedAssetMngDataWithDocs
        };

        let saveChartData = {
            ...chartData,
            properties_text: JSON.stringify(tempSaveChartData.properties_text)
        };
        if (selectedExportProperty) {
            saveChartData.template_property_id = selectedExportProperty;
        }
        if (selectedExportTemplate) {
            saveChartData.template_id = selectedExportTemplate;
        }
        saveChartData = {
            ...saveChartData,
            client_id: selectedClient,
            property_type: "SKEL_PROP",
            skelton_property_id: selectedCoreReport?.id,
            user: localStorage.getItem("userId"),
            is_export: true,
            skelton_property_type: selectedCoreReport?.property_type,
            export_for_each:
                selectedExportType === "MultipleDataSet"
                    ? CoreReportGeohierarchy?.filter(k => Object.keys(chartData?.properties_text?.project?.band1?.mfilter).includes(k.paramKey)).pop()
                          ?.paramKey
                    : null,
            orientation: Object.keys(chartData?.properties_text?.project?.band1?.type || {}).some(
                key =>
                    key.includes("user_doc") ||
                    key.includes("multi_recommendation") ||
                    key.includes("normal_recom") ||
                    key.includes("sorted_recom") ||
                    key.includes("recom_onepager") ||
                    key.includes("building_report") ||
                    key.includes("addition_report") ||
                    key.includes("site_report") ||
                    key?.startsWith("recommendations_site") ||
                    key?.startsWith("recommendations_building") ||
                    key?.startsWith("recommendations_region") ||
                    key?.startsWith("recommendations_project") ||
                    key?.startsWith("recommendations_addition") ||
                    key?.startsWith("asset_site") ||
                    key?.startsWith("asset_building") ||
                    key?.startsWith("asset_region") ||
                    key?.startsWith("asset_project") ||
                    key?.startsWith("asset_addition")
            ),
            export_type: exportType,
            is_pdf: selectedFileExtension === "is_pdf" ? true : false,
            is_excel: selectedFileExtension === "is_excel" ? true : false,
            ...(exportType === 2 && restParams)
        };
        corereportExport.current = true;
        await dispatch(actions.createReportFromCoreReport(saveChartData));
        // showLongAlert("Report generation initiated and will be ready for download shortly. Please click the Refresh button to check for status.");
    };

    const updateDataWithUserDocs = (entityTypeData, entity) => {
        let updatedData = Object.keys(entityTypeData).reduce((resultData, currentProp, index) => {
            if (currentProp.includes("multi_recommendation")) {
                const { number_of_recom, ...restRecomBandData } = entityTypeData[currentProp];
                return {
                    ...resultData,
                    ["multi_recommendation" + (index + 1)]: {
                        ...restRecomBandData,
                        mfilter: {
                            ...entityTypeData?.[currentProp]?.mfilter,
                            ...chartData.properties_text.project.band1.mfilter
                        },
                        key_name:
                            entityTypeData[currentProp]?.key_name &&
                            typeof entityTypeData[currentProp].key_name === "object" &&
                            entityTypeData[currentProp]?.key_name !== null
                                ? {
                                      ...entityTypeData[currentProp]?.key_name,
                                      ...getKeyNamesForMultiRecom()
                                  }
                                : { ...getKeyNamesForMultiRecom() }
                    }
                };
            }
            if (currentProp.includes("normal_recom")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {} } };
                const { number_of_recom, ...restRecomBandData } = currentData?.band1;
                return {
                    ...resultData,
                    ["normal_recom" + (index + 1)]: {
                        band1: {
                            ...restRecomBandData,
                            mfilter: {
                                ...currentData?.band1?.mfilter,
                                ...chartData.properties_text.project.band1.mfilter,
                                client_ids: [selectedClient]
                            }
                        }
                    }
                };
            }
            if (currentProp.includes("recom_onepager")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {} } };
                const { number_of_recom, ...restRecomBandData } = currentData?.band1;
                return {
                    ...resultData,
                    ["recom_onepager" + (index + 1)]: {
                        band1: {
                            ...restRecomBandData,
                            mfilter: {
                                ...currentData?.band1?.mfilter,
                                ...chartData.properties_text.project.band1.mfilter
                            }
                        }
                    }
                };
            }
            if (currentProp.includes("asset-onepager")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {} } };
                const { number_of_recom = [], ...restRecomBandData } = currentData?.band1 ?? {};
                return {
                    ...resultData,
                    ["asset-onepager" + (index + 1)]: {
                        band1: {
                            ...restRecomBandData,
                            mfilter: {
                                ...currentData?.band1?.mfilter,
                                ...chartData.properties_text[entity].band1.mfilter,
                                client_ids: [selectedClient]
                            }
                        }
                    }
                };
            }
            if (currentProp.includes("building_report")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {}, name: "BBI" } };
                const { ...restRecomBandData } = currentData?.band1;
                return {
                    ...resultData,
                    ["building_report" + (index + 1)]: {
                        band1: {
                            ...restRecomBandData,
                            mfilter: {
                                ...currentData?.band1?.mfilter,
                                ...chartData.properties_text.project.band1.mfilter
                            }
                        }
                    }
                };
            }
            if (currentProp.includes("addition_report")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {}, name: "BAI" } };
                const { ...restRecomBandData } = currentData?.band1;
                return {
                    ...resultData,
                    ["addition_report" + (index + 1)]: {
                        band1: {
                            ...restRecomBandData,
                            mfilter: {
                                ...currentData?.band1?.mfilter,
                                ...chartData.properties_text.project.band1.mfilter
                            }
                        }
                    }
                };
            }
            if (currentProp.includes("site_report")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {}, name: "BSI" } };
                const { ...restRecomBandData } = currentData?.band1;
                return {
                    ...resultData,
                    ["site_report" + (index + 1)]: {
                        band1: {
                            ...restRecomBandData,
                            mfilter: {
                                ...currentData?.band1?.mfilter,
                                ...chartData.properties_text.project.band1.mfilter
                            }
                        }
                    }
                };
            }
            if (currentProp.includes("system")) {
                let currentData = entityTypeData[currentProp] || { band1: { mfilter: {} } };
                const { normal_recom, ...restRecomBandData } = currentData;
                return {
                    ...resultData,
                    [currentProp]: {
                        ...restRecomBandData,
                        ...(normal_recom && {
                            normal_recom: {
                                ...currentData?.normal_recom,
                                band1: {
                                    ...currentData?.normal_recom?.band1,
                                    mfilter: {
                                        ...currentData?.normal_recom?.band1?.mfilter,
                                        ...chartData.properties_text.project.band1.mfilter
                                    }
                                }
                            }
                        })
                    }
                };
            }
            if (currentProp?.includes("asset_band")) {
                let currentData = entityTypeData?.[currentProp] || { band1: { mfilter: {} } };
                return {
                    ...resultData,
                    [currentProp]: {
                        ...currentData,
                        mfilter: {
                            ...currentData?.band1?.mfilter,
                            ...chartData?.properties_text?.[entity]?.band1?.mfilter
                        }
                    }
                };
            }
            return { ...resultData, [currentProp]: entityTypeData[currentProp] };
        }, {});
        return updatedData;
    };

    const getKeyNamesForMultiRecom = () => {
        let tempKeys = chartData?.properties_text?.project?.band1?.filter_label
            ? { ...chartData?.properties_text?.project?.band1?.filter_label }
            : {};
        let removeKey = Object.keys(tempKeys).find(keyValue => keyValue.toLowerCase().includes("project")) || null;
        if (removeKey) {
            delete tempKeys[removeKey];
        }
        return tempKeys;
    };

    const showLongAlert = msg => showAlertUtil(msg, true);
    const renderExportTypeSelectModal = () => {
        if (!showExportTypeSelectModal) return null;
        return (
            <Portal
                body={
                    <ExportModal
                        onCancel={() => setShowExportTypeSelectModal(false)}
                        onSelection={(type, file, extension, restParams) => onExportTypeSelection(type, file, extension, restParams)}
                        requiredFields={requiredFields}
                        chartData={chartData}
                        propertyData={selectedCoreReport.properties_text ? JSON.parse(selectedCoreReport.properties_text) : {}}
                    />
                }
                onCancel={() => setShowExportTypeSelectModal(false)}
            />
        );
    };

    const onExportTypeSelection = (selectedExportType, selectedFileType, selectedFileExtension, restParams) => {
        setShowExportTypeSelectModal(false);
        if (selectedExportType === "MultipleDataSet") {
            if (selectedFileType === "SeparateFile") {
                exportReportConfirm(2, selectedExportType, selectedFileExtension, restParams);
            } else {
                exportReportConfirm(3, selectedExportType, selectedFileExtension, restParams);
            }
        } else {
            exportReportConfirm(1, selectedExportType, selectedFileExtension, restParams);
        }
    };

    const getDropdownTooltip = (itemId = "", optionArray = []) => {
        const item = optionArray?.find(option => option.id === itemId);
        let content = item?.name;
        if (optionArray?.some(option => option?.sm_default)) {
            if (item?.sm_default) {
                content += ` (Active)`;
            }
        } else if (item?.default) {
            content += ` (Active)`;
        }
        return content;
    };

    const submitHeaderConfigModal = (header_data = []) => {
        setChartData(prevState => {
            const prevBand = prevState.properties_text?.project?.band1 || {};
            let updatedType = prevBand?.type || {};

            if (!header_data?.length) {
                updatedType = Object.keys(updatedType).reduce((acc, key) => {
                    const item = updatedType[key];
                    acc[key] = key.startsWith("user_doc")
                        ? {
                              ...item,
                              include_custom_header: false
                          }
                        : item;

                    return acc;
                }, {});
            }

            return {
                ...prevState,
                properties_text: {
                    ...prevState.properties_text,
                    project: {
                        ...prevState.properties_text?.project,
                        band1: {
                            ...prevBand,
                            custom_header_keys: header_data || [],
                            type: updatedType
                        }
                    }
                }
            };
        });

        setShowHeadingConfig(false);
    };
    const renderHeadingConfigModal = () => {
        if (!showHeadingConfig) return null;
        return (
            <Portal
                body={
                    <HeaderConfigModal
                        mfilter={chartData?.properties_text?.project?.band1?.mfilter}
                        headerConfigData={chartData?.properties_text?.project?.band1?.custom_header_keys}
                        onClose={() => setShowHeadingConfig(false)}
                        onSubmit={header_data => {
                            submitHeaderConfigModal(header_data);
                        }}
                        isReadOnly={true}
                    />
                }
                onCancel={() => {}}
            />
        );
    };
    const isExcel = chartData?.properties_text?.project?.band1?.export_type === "excel" ?? false;
    return (
        <div class="modal modal-region smart-chart-popup smart-dtl-pop core-rpt-popup" id="modalId" tabIndex="-1" style={{ display: "block" }}>
            <ReactTooltip
                id="core-report"
                className="rc-tooltip-custom-class"
                backgroundColor="#007bff"
                place="right"
                effect="solid"
                delayShow={500}
            />
            <Draggable handle=".modal-header">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div class="modal-header draggable">
                            <h5 class="modal-title" id="exampleModalLabel">
                                <div class="txt-hed">
                                    Create Report From Core report{" "}
                                    {/* {selectedCoreReport?.entityExportType ? "(Multiple Data Set)" : "(Single Data Set)"} */}
                                </div>
                                <div class="btn-mod-headotr">
                                    <button class="btn-edit btn-exp" data-for="edit-report-template-modal" onClick={() => exportReport()}>
                                        Export
                                    </button>
                                    <button class="btn-cancel" onClick={() => onCancel()}>
                                        Close
                                    </button>
                                </div>
                            </h5>
                        </div>
                        <div class="modal-body region-otr dshb core-report">
                            <div class="tab-dtl region-mng chart-drage-area">
                                <div class="tab-active location-sec smart-detail-scroll height-scroll">
                                    <div className="row m-0">
                                        <div className="col-md-12">
                                            <div className="chart-hed-main">
                                                <div className="form-row">
                                                    <div className="form-group col-4">
                                                        <label>Client</label>
                                                        <select
                                                            className={`form-control dropdown export-prop-select cursor-diabled`}
                                                            value={selectedClient}
                                                            name="font_id"
                                                            onChange={e => handleSelectClient(e.target.value)}
                                                            disabled={true}
                                                            data-for="core-report"
                                                            data-tip={getDropdownTooltip(selectedClient, getClientDropDownDataResponse?.data)}
                                                        >
                                                            <option value="">Select</option>
                                                            {getClientDropDownDataResponse?.data?.map(p => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="form-group col-4">
                                                        <label>Export Property</label>
                                                        <select
                                                            className={`${
                                                                showErrorBorder && !selectedExportProperty?.trim()?.length ? "error-border" : ""
                                                            } form-control dropdown export-prop-select`}
                                                            value={selectedExportProperty}
                                                            name="font_id"
                                                            onChange={e => {
                                                                let value = e.target.value;
                                                                setSelectedExportProperty(value);
                                                            }}
                                                            data-for="core-report"
                                                            data-tip={getDropdownTooltip(
                                                                selectedExportProperty,
                                                                getTemplatePropertiesListResponse?.data
                                                            )}
                                                        >
                                                            <option value="">Select</option>
                                                            {selectedClient &&
                                                                getTemplatePropertiesListResponse?.data?.map(p => (
                                                                    <option key={p.id} value={p.id}>
                                                                        {p.name}{" "}
                                                                        {getTemplatePropertiesListResponse?.data?.some(temp => temp?.sm_default)
                                                                            ? p?.sm_default
                                                                                ? "(Active)"
                                                                                : ""
                                                                            : p?.default
                                                                            ? "(Active)"
                                                                            : ""}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                    <div className="form-group col-4">
                                                        <label>Export Template</label>
                                                        <select
                                                            className={`${
                                                                showErrorBorder && !selectedExportTemplate?.trim()?.length ? "error-border" : ""
                                                            } form-control dropdown export-prop-select`}
                                                            value={selectedExportTemplate}
                                                            name="font_id"
                                                            onChange={e => {
                                                                let value = e.target.value;
                                                                setSelectedExportTemplate(value);
                                                            }}
                                                            data-for="core-report"
                                                            data-tip={getDropdownTooltip(selectedExportTemplate, getTemplateListResponse?.data)}
                                                        >
                                                            <option value="">Select</option>
                                                            {selectedClient &&
                                                                getTemplateListResponse?.data?.map(p => {
                                                                    return (
                                                                        <option key={p.id} value={p.id}>
                                                                            {p.name}{" "}
                                                                            {getTemplateListResponse?.data?.some(temp => temp?.sm_default)
                                                                                ? p?.sm_default
                                                                                    ? "(Active)"
                                                                                    : ""
                                                                                : p?.default
                                                                                ? "(Active)"
                                                                                : ""}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                    </div>
                                                    <div className="form-group col-4">
                                                        <label>Name</label>
                                                        <input
                                                            type="text"
                                                            className={`${
                                                                (showErrorBorder && !chartData.name.trim().length) ||
                                                                (showErrorBorder && chartData.name.trim().length > 100)
                                                                    ? "error-border"
                                                                    : ""
                                                            } form-control`}
                                                            value={chartData.name}
                                                            onChange={e => {
                                                                let value = e.target.value;
                                                                setChartData(prevChartData => {
                                                                    return {
                                                                        ...prevChartData,
                                                                        name: value
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                        {showErrorBorder && chartData.name.trim().length > 100 && (
                                                            <small className="text-danger pl-2 er-danger">
                                                                Name should not exceed 100 characters.
                                                            </small>
                                                        )}
                                                    </div>
                                                    <div className="form-group col-4">
                                                        <label>Notes</label>
                                                        <textarea
                                                            className="form-control textarea area-hgt"
                                                            value={chartData.notes}
                                                            onChange={e => {
                                                                let value = e.target.value;
                                                                setChartData(prevChartData => {
                                                                    return {
                                                                        ...prevChartData,
                                                                        notes: value
                                                                    };
                                                                });
                                                            }}
                                                        ></textarea>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row m-0">
                                        <div className="col-md-12">
                                            <div id="accordion">
                                                {Object.keys(chartData.properties_text)
                                                    ?.filter(
                                                        entityKey => Object.keys(chartData.properties_text?.[entityKey]?.band1?.type || {}).length
                                                    )
                                                    .map((entity, index) => (
                                                        <div class="card">
                                                            <div class={`card-header`} id={`headingOne${entity}`}>
                                                                <div class="icon">
                                                                    <img src="/img/icon-squre.svg" />
                                                                </div>
                                                                <div
                                                                    class="heading-text"
                                                                    data-toggle="collapse"
                                                                    aria-expanded={!isCollapsed[entity]}
                                                                    onClick={() => {
                                                                        setIsCollapsed(prevState => ({
                                                                            ...prevState,
                                                                            [entity]: !prevState[entity]
                                                                        }));
                                                                        entity === "project" && setShowFilter(!showFilter);
                                                                    }}
                                                                >
                                                                    <h3>{getDisplayNameFromKey(entity)}</h3>
                                                                    {entity === "project" && showFilter && (
                                                                        <div className="btn-sec check-box-area">
                                                                            <button
                                                                                className={`filter-button blue-common-btn cursor-pointer btn-add-img ml-2 fil-core `}
                                                                                data-tip={`View Filters`}
                                                                                data-for="dashboard-icons-right"
                                                                                onClick={e => {
                                                                                    e.stopPropagation();
                                                                                    const mfilter =
                                                                                        chartData?.properties_text?.["project"]?.band1?.mfilter || {};
                                                                                    for (const [key, value] of Object.entries(mfilter)) {
                                                                                        if (key === "building_types") {
                                                                                            const { [key]: omit, ...rest } = mfilter;

                                                                                            dispatch(dashboardActions?.getMasterFilter(rest, key));
                                                                                        }

                                                                                        setFilterOpen(true);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <img src={FilterIcon} alt="" />
                                                                                <span> Filters</span>
                                                                                {chartData.properties_text?.[entity]?.band1?.mfilter &&
                                                                                    Object.keys(chartData.properties_text?.[entity]?.band1?.mfilter)
                                                                                        ?.length > 0 && (
                                                                                        <span className="notifyTxt">
                                                                                            {Object.keys(
                                                                                                chartData.properties_text?.[entity]?.band1?.mfilter
                                                                                            )?.reduce((count, key) => {
                                                                                                if (
                                                                                                    [
                                                                                                        "client_ids",
                                                                                                        "end_year",
                                                                                                        "update_fci"
                                                                                                    ]?.includes(key)
                                                                                                )
                                                                                                    return count;
                                                                                                if (key === "filters") {
                                                                                                    const filterObjects = Object.values(
                                                                                                        chartData.properties_text?.[entity]?.band1
                                                                                                            ?.mfilter[key]
                                                                                                    );
                                                                                                    const filteredObjects = filterObjects.filter(
                                                                                                        obj =>
                                                                                                            ((obj?.key !== "" && obj?.key !== null) ||
                                                                                                                ((obj?.filters?.[0] === "null" ||
                                                                                                                    obj?.filters?.[0] ===
                                                                                                                        "not_null" ||
                                                                                                                    obj?.filters?.[0] ===
                                                                                                                        "count_gt_1") &&
                                                                                                                    (obj?.key === "" ||
                                                                                                                        obj?.key === null))) &&
                                                                                                            obj?.filters?.length > 0
                                                                                                    );
                                                                                                    return count + filteredObjects?.length;
                                                                                                }
                                                                                                return (key === "start_year" &&
                                                                                                    chartData.properties_text?.[entity]?.band1
                                                                                                        ?.mfilter[key]) ||
                                                                                                    (Array.isArray(
                                                                                                        chartData.properties_text?.[entity]?.band1
                                                                                                            ?.mfilter[key]
                                                                                                    ) &&
                                                                                                        chartData.properties_text?.[entity]?.band1
                                                                                                            ?.mfilter[key]?.length > 0) ||
                                                                                                    (key === "top_n" &&
                                                                                                        chartData.properties_text?.[entity]?.band1
                                                                                                            ?.mfilter?.top_n?.[
                                                                                                            "recommendations.project_total"
                                                                                                        ])
                                                                                                    ? count + 1
                                                                                                    : count;
                                                                                            }, 0)}
                                                                                        </span>
                                                                                    )}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {entity === "project" && showFilter && !isExcel ? (
                                                                    <button
                                                                        class={`filter-button blue-common-btn heading-blue-button ${(chartData?.properties_text?.project?.band1?.custom_header_keys || [])?.length ? "filter-button" : ""}`}
                                                                        data-tip="View Filters"
                                                                        data-for="dashboard-icons-right"
                                                                        currentitem="false"
                                                                        onClick={() => {
                                                                            setShowHeadingConfig(true);
                                                                        }}
                                                                    >
                                                                        <span className={`pr-2 pl-2`}> Page Header Config</span>
                                                                        {(chartData?.properties_text?.project?.band1?.custom_header_keys || [])
                                                                            ?.length > 0 && (
                                                                            <span className="notifyTxt">
                                                                                {
                                                                                    (
                                                                                        chartData?.properties_text?.project?.band1
                                                                                            ?.custom_header_keys || []
                                                                                    )?.length
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </button>
                                                                ) : null}
                                                            </div>
                                                            <div
                                                                id={`collapseOne${entity}`}
                                                                className={`collapse ${!isCollapsed[entity] ? "show" : ""}`}
                                                                aria-labelledby={`headingOne${entity}`}
                                                            >
                                                                <div className="card-body">
                                                                    <ChartSection
                                                                        masterFilterList={masterFilterList}
                                                                        chartSectionData={chartData.properties_text?.[entity]?.band1}
                                                                        entity={entity}
                                                                        selectedClient={selectedClient}
                                                                        getMasterFiltersData={getMasterFiltersData}
                                                                        setSelectedMasterFilters={setSelectedMasterFilters}
                                                                        requiredFields={requiredFields}
                                                                        setFilterOpen={setFilterOpen}
                                                                        filterOpen={filterOpen}
                                                                        projectSectionDatas={chartData.properties_text?.["project"]?.band1}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
            {renderExportTypeSelectModal()}
            {renderHeadingConfigModal()}
        </div>
    );
};

export default CreateReportFromCoreReportsModal;
